import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import products, { categories } from '../data/products';
import ProductCard from '../components/products/ProductCard';
import { AnimatedQualityIcon, AnimatedRangeIcon, AnimatedDeliveryIcon } from '../components/common/AnimatedIcons';
import './Home.css';

const Home = () => {
  const featuredProducts = products.slice(0, 3);
  const isAutoPlaying = true;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const categoryImages = {
    "Shuttering": "/Product images/MS Shuttering Plate.png",
    "Props": "/Product images/Adjustable Steel Prop .png",
    "Scaffolding": "/Product images/Cuplock Standard.png",
    "Jacks": "/Product images/Base Jack.png",
    "Accessories": "/Product images/Double Coupler.png"
  };

  const activeCategories = categories.slice(0, 5);
  // Triple the categories for a seamless infinite scroll effect
  const displayCategories = [...activeCategories, ...activeCategories, ...activeCategories];
  const intervalTime = 4000;

  useEffect(() => {
    if (!isAutoPlaying) {
      setProgress(0);
      return;
    }

    let requestRef;
    let startTime;

    const animate = (time) => {
      if (!startTime) startTime = time;
      const elapsed = time - startTime;
      const newProgress = Math.min((elapsed / intervalTime) * 100, 100);

      setProgress(newProgress);

      if (elapsed >= intervalTime) {
        setCurrentIndex((prev) => {
          const nextIndex = prev + 1;
          if (nextIndex >= activeCategories.length * 2) {
            return activeCategories.length;
          }
          return nextIndex;
        });
        startTime = time; // Reset for next slide
      }

      requestRef = requestAnimationFrame(animate);
    };

    requestRef = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef);
  }, [currentIndex, isAutoPlaying, activeCategories.length]);

  // Initial state: start at the first item of the second set
  useEffect(() => {
    setCurrentIndex(activeCategories.length);
  }, [activeCategories.length]);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-video-container">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="hero-video"
          >
            <source src="/Hero_Video.mp4" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
        </div>
        <div className="container hero-content">
          <h1>Industrial Strength <br /><span className="highlight">Shuttering & Scaffolding</span></h1>
          <p>Premium quality MS Shuttering plates, Props, Spans, and Scaffolding systems for large-scale construction projects.</p>
          <div className="hero-btns">
            <Link to="/products" className="btn btn-primary">View Catalogue</Link>
            <Link to="/contact" className="btn btn-outline">Get a Quote</Link>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="section categories-section">
        <div className="container">
          <div className="section-header-row">
            <h2 className="text-center">Our Product Categories</h2>
          </div>

          {!isAutoPlaying ? (
            <div className="category-grid">
              {activeCategories.map((cat, index) => (
                <Link to="/products" key={index} className="category-card-static">
                  <div className="category-image-wrapper">
                    <img src={categoryImages[cat]} alt={cat} className="original-aspect" />
                  </div>
                  <div className="category-label-static">
                    <h3>{cat}</h3>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="carousel-view">
              <div className="carousel-viewport">
                <div
                  className="carousel-track"
                  style={{
                    transform: `translateX(calc(50% - (${currentIndex} * var(--slide-width)) - (1.5 * var(--slide-width))))`
                  }}
                >
                  {displayCategories.map((cat, index) => {
                    const isActive = index >= currentIndex && index < currentIndex + 3;
                    return (
                      <div
                        key={index}
                        className={`carousel-item ${isActive ? 'active-item' : ''}`}
                      >
                        <Link to="/products" className="carousel-link">
                          <div className="carousel-image-box">
                            <img
                              src={categoryImages[cat]}
                              alt={cat}
                              className="carousel-image"
                            />
                          </div>
                          <div className="carousel-label-below">
                            <h3>{cat}</h3>
                            <span className="view-btn">View More</span>
                          </div>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="carousel-dots-wrapper">
                {activeCategories.map((_, index) => {
                  const virtualIndex = currentIndex % activeCategories.length;
                  return (
                    <div
                      key={index}
                      className={`carousel-dot-item ${index === virtualIndex ? 'active' : ''}`}
                      onClick={() => setCurrentIndex(activeCategories.length + index)}
                    >
                      <div className="dot-track">
                        {index === virtualIndex && (
                          <div className="dot-fill" style={{ width: `${progress}%` }}></div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* About Neha Shuttering */}
      <section
        className="section about-neha"
        style={{ backgroundImage: "url('/aboutus.png')" }}
      >
        <div className="container about-grid-modern">
          <div className="about-text-content">
            <h2 className="modern-title">
              <span className="small-text">About</span>
              <div className="about-logos-wrapper">
                <img src="/logo.png" alt="Neha Shuttering" className="about-section-logo" />
                <img src="/deslogo.png" alt="Design Logo" className="about-section-deslogo" />
              </div>
            </h2>
            <div className="title-underline"></div>
            <p className="about-description">
              Neha Shuttering Store is a trusted name in the shuttering and scaffolding industry, proudly serving the construction sector since 1985. With 40+ years of experience, we provide reliable solutions for residential, commercial, and infrastructure projects.
            </p>
            <Link to="/about" className="btn btn-outline">Learn More About Us</Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section featured-products bg-light">
        <div className="container">
          <h2 className="text-center">Featured Products</h2>
          <div className="grid grid-3">
            {featuredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          <div className="text-center mt-40">
            <Link to="/products" className="btn btn-primary">View All Products</Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section why-us">
        <div className="container">
          <h2 className="text-center">Why Customers Choose Us</h2>
          <div className="grid grid-3">
            <div className="feature-item text-center">
              <AnimatedQualityIcon />
              <h3>Premium Quality</h3>
              <p>We use high-grade steel and advanced manufacturing processes to ensure maximum durability.</p>
            </div>
            <div className="feature-item text-center">
              <AnimatedRangeIcon />
              <h3>Wide Product Range</h3>
              <p>From shuttering plates to complex scaffolding systems, we provide a complete solution.</p>
            </div>
            <div className="feature-item text-center">
              <AnimatedDeliveryIcon />
              <h3>Timely Delivery</h3>
              <p>Our efficient logistics ensure that your construction project stays on schedule.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section contact-cta">
        <div className="container text-center">
          <h2>Ready to Start Your Project?</h2>
          <p>Get in touch with our experts for a customized quote and expert advice.</p>
          <Link to="/contact" className="btn btn-primary btn-large">Contact Our Sales Team</Link>
        </div>
      </section>
    </div>
  );
};

export default Home;